import RowStack from '@common-components/common/row-stack/RowStack';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Send, RefreshCw } from 'lucide-react';
import { useAppDispatch } from '@common/reducers/hooks/store.hook';
import { handleMentionsFormatInMessage } from '@common/services/utils';
import WhatsappContactBox from '@common/views/whatsapp-view/whatsapp-contact-box';
import { sendScheduleMessageThunk } from '@extension-context-content/thunks/WhatsappSendMessageThunk';
import { useRetryMessageMutation } from '@common-reducers/backend-api/backend-api';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';


interface CampaignMessageItemRowProps {
    message: any;

}



export default function CampaignMessageItemWaitingOrNotSentRow({ message }: CampaignMessageItemRowProps) {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(namespace);

    const contactWAId = message.contactList?.[0]?.id;
    const contactName = message.contactList?.[0]?.name ?? `${message.contactList?.[0]?.firstName} ${message.contactList?.[0]?.lastName}`;

    // RTK Query mutation for retry - posts to backend
    const [retryMessage, { isLoading: isRetrying }] = useRetryMessageMutation();

    // Handle send now - always uses backend API
    const handleSendNow = async (e: React.MouseEvent) => {
        e.stopPropagation();
        
        if (!message?._id) {
            console.error('Cannot send: message _id is missing');
            return;
        }

        try {
            await retryMessage({
                messageId: message._id
            }).unwrap();
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    return (
        <RowStack
            className={cn(
                "p-3 border-b border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)]",
                "justify-between"
            )}
        >
            <RowStack className="font-normal" gap={2}>
                <WhatsappContactBox
                    whatsappId={contactWAId}
                    contactName={contactName}
                    message={handleMentionsFormatInMessage(message.message)}
                    messageScheduledCount={message.messageScheduledCount}
                    messageApiCount={message.messageApiCount}
                    messageCampaignCount={message.messageCampaignCount}
                    dueDate={message.dueDate}
                    profilePicURL={message.contactList?.[0]?.profilePicUrl}
                />

                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                // FE#360 — a person pressed send. Without isSendMessageNow the
                                // Status at-most-once ledger would decline this as if it were an
                                // automatic re-dispatch, and the button would do nothing.
                                onClick={() => dispatch(sendScheduleMessageThunk({ userMessage: message, isSendMessageNow: true }))}
                                className={cn(
                                    "mr-1.25 p-0 w-7 h-7 rounded-full",
                                    "flex items-center justify-center",
                                    "bg-gradient-to-br from-[#6dd1f1] to-[var(--tasks-button-hover-bg)]",
                                    "hover:from-[rgba(109,209,241,0.7)] hover:to-[rgba(37,154,233,0.7)]",
                                    "shadow-[0_2px_2.5px_rgba(0,0,0,0.13)]",
                                    "text-white dark:text-white",
                                    "transition-all duration-200"
                                )}
                            >
                                <Send className="h-3.75 w-3.75" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="text-left">
                            {t('bulkDashboard.sendMessageNow')}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 mr-1.25"
                                onClick={handleSendNow}
                                disabled={isRetrying}
                            >
                                {isRetrying ? (
                                    <Loader2 className="h-3.75 w-3.75 text-slate-500 dark:text-slate-400 animate-spin" />
                                ) : (
                                    <RefreshCw className="h-3.75 w-3.75 text-slate-500 dark:text-slate-400" />
                                )}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="text-left">
                            <p>{t('bulkDashboard.sendNow')}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </RowStack>
        </RowStack>
    );
}
